import { Button, Input, Select, TableProps } from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import PaymentIcon from "../../../assets/icons/sidebar/PaymentIcon";
import StatusItem from "../../../components/common/StatusItem";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import { GetAllPaymentParamsType } from "../../../features/organization/payment/payment.type";
import {
  useGetAllPaymentQuery,
  useSendToPaymentPortalMutation,
} from "../../../features/organization/payment/paymentApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../utils/number";
import { valueOrNa } from "../../../utils/string";
import { addKeyInArray, selectSearchOption } from "../../../utils/utils";
import logo from "./_images/payment-logo.png";
const Payment = () => {
  const [selectedData, setSelectedData] = useState<number[]>([]);
  const {
    page,
    limit,
    handleLimit,
    handlePagination,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllPaymentParamsType>();
  const { user } = useAppSelector((state) => state.auth);
  const { open, handleOpen, handleClose } = useModal<number[], "CONFIRM">();
  const { isLoading, isError, error, data } = useGetAllPaymentQuery({
    params: { ...filter, business_id: user?.business?.id, search: keyword },
  });
  const [
    sendToPaymentPortal,
    {
      isLoading: isSending,
      isError: isSendError,
      error: sendError,
      isSuccess: isSendSuccess,
    },
  ] = useSendToPaymentPortalMutation();
  useError(isError, error, "Get payment fail");
  useError(isSendError, sendError, "Send payment fail");

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Name",
      dataIndex: "business_member_name",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Email",
      dataIndex: "business_member_email",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Amount (BDT)",
      dataIndex: "amount",
      render: (val) => addCommaInNumber(val),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : "n/a"),
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val) => <StatusItem value={val} list={paymentStatusList} />,
    },
  ];

  useEffect(() => {
    if (isSendSuccess) {
      toast("success", "Payment sent successfully");
      setSelectedData([]);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendSuccess]);

  return (
    <SidebarLayout>
      {open?.type === "CONFIRM" && (
        <ConfirmModal
          open={open.open}
          data={open.data}
          handleClose={handleClose}
          title="Send to Disbursement"
          subTitle="Are you sure you want to proceed with disbursement?"
          okText="Confirm"
          handleOk={(values) => {
            sendToPaymentPortal({ data: { payment_ids: values } });
          }}
          okLoading={isSending}
        />
      )}
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total}
        dataSource={addKeyInArray(data?.data?.payments)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as number[]);
          },
          getCheckboxProps: (record) => {
            return {
              disabled: record?.status !== "pending",
            };
          },
          columnWidth: 40,
        }}
        filterSection={
          <>
            {!!user?.business?.logo && (
              <img className="h-12" src={user?.business?.logo || logo} alt="" />
            )}
            {/* <div className="text-right -space-y-1.5">
              <span className="block text-lg text-[#001652] font-semibold">
                0 BDT
              </span>
              <span className="block text-sm text-display-dark font-medium">
                Balance
              </span>
            </div> */}
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={paymentStatusList}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter.status}
              onChange={(val) => handleFilter("status", val)}
              popupMatchSelectWidth={180}
            />
            <Button
              className="w-full"
              type="primary"
              disabled={!selectedData.length}
              onClick={() => {
                if (selectedData?.length)
                  handleOpen({ type: "CONFIRM", data: selectedData });
              }}
            >
              <PaymentIcon /> Send to Disbursement
            </Button>
          </>
        }
      />
    </SidebarLayout>
  );
};

export default Payment;

export const paymentStatusList = [
  {
    label: "Pending",
    value: "pending",
    className: "bg-[#FFF9DF] text-[#D8A800]",
  },
  {
    label: "Completed",
    value: "completed",
    className: "bg-[#1DB4691F] text-[#11A75C]",
  },
  {
    label: "Failed",
    value: "failed",
    className: "bg-[#FFF5F4] text-[#FF3838]",
  },
  {
    label: "Send to Disbursement",
    value: "send_to_disbursement",
    className: "bg-[#F2F5FF] text-[#4A72FF]",
  },
];
